"use client"

import { useState, useRef } from "react"
import { prepararAntispam, tokenAntispam } from "@/lib/antispam/cliente"
import { CampoTrampa } from "@/components/antispam/campo-trampa"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { toast } from "sonner"

const contactSchema = z.object({
    name: z.string().min(2),
    email: z.string().email(),
    subject: z.string().min(5),
    message: z.string().min(10),
})

type ContactFormValues = z.infer<typeof contactSchema>

interface ContactFormProps {
    dict: any
}

export function ContactForm({ dict }: ContactFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)
    const t = dict.about.contactPage

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<ContactFormValues>({
        resolver: zodResolver(contactSchema),
    })

    async function onSubmit(data: ContactFormValues) {
        setIsSubmitting(true)
        try {
            const trampa = formRef.current ? new FormData(formRef.current).get("sitio_web") : null
            const token = await tokenAntispam("contacto")
            const response = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...data, sitio_web: trampa, token }),
            })

            if (!response.ok) {
                // Los frenos contra el spam dicen que hacer (recargar, quitar enlaces...).
                const cuerpo = await response.json().catch(() => ({}))
                if (cuerpo?.error && response.status !== 500) {
                    toast.error(cuerpo.error)
                    return
                }
                throw new Error("Failed to send message")
            }

            toast.success(t.form.success)
            reset()
        } catch (error) {
            toast.error(t.form.error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="grid gap-16 lg:grid-cols-2">
            {/* Contact Form */}
            <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200">
                <form ref={formRef} onSubmit={handleSubmit(onSubmit)} onFocus={prepararAntispam} className="relative space-y-6">
                    <CampoTrampa />
                    <div className="grid gap-6 sm:grid-cols-2">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                {t.form.name}
                            </label>
                            <input
                                {...register("name")}
                                type="text"
                                id="name"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm px-4 py-2 border"
                                disabled={isSubmitting}
                            />
                            {errors.name && (
                                <p className="mt-1 text-sm text-red-600">{t.form.required}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                {t.form.email}
                            </label>
                            <input
                                {...register("email")}
                                type="email"
                                id="email"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm px-4 py-2 border"
                                disabled={isSubmitting}
                            />
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-600">{t.form.required}</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                            {t.form.subject}
                        </label>
                        <input
                            {...register("subject")}
                            type="text"
                            id="subject"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm px-4 py-2 border"
                            disabled={isSubmitting}
                        />
                        {errors.subject && (
                            <p className="mt-1 text-sm text-red-600">{t.form.required}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                            {t.form.message}
                        </label>
                        <textarea
                            {...register("message")}
                            id="message"
                            rows={4}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm px-4 py-2 border"
                            disabled={isSubmitting}
                        />
                        {errors.message && (
                            <p className="mt-1 text-sm text-red-600">{t.form.required}</p>
                        )}
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isSubmitting ? t.form.sending : t.form.send}
                        </button>
                    </div>
                </form>
            </div>

            {/* Support Info */}
            <div className="flex flex-col justify-center space-y-8">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t.support.title}</h2>
                    <p className="mt-4 text-lg text-gray-600">
                        {t.support.text}
                    </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-8 border border-slate-200">
                    <dl className="space-y-6">
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Email</dt>
                            <dd className="mt-2 text-base font-semibold text-primary">
                                <a href={`mailto:${t.support.email}`} className="hover:underline">
                                    {t.support.email}
                                </a>
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">{t.support.scheduleLabel}</dt>
                            <dd className="mt-2 text-base text-gray-900">{t.support.hours}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>
    )
}
